import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { ArrowRight, FolderKanban, Search } from 'lucide-react';
import type { Project } from '@seo/types';
import { Page, PageHeader, PageToolbar } from '@/components/ui/page';
import { Surface } from '@/components/ui/surface';
import { EmptyState } from '@/components/ui/empty';
import { ErrorBanner } from '@/components/error-banner';
import { Spinner } from '@/components/spinner';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { fadeUp, stagger } from '@/components/motion';
import { hostname, relTime } from '@/lib/format';
import { ProjectCreateButton } from '@/features/projects/components/project-create-button';
import { useProjects } from '@/features/projects/use-projects';
import { useAudits } from '@/features/audits/use-audits';

export function ProjectsRoute(): JSX.Element {
  const [query, setQuery] = useState('');
  const projects = useProjects();
  const audits = useAudits();

  const filtered = useMemo(() => {
    const items = projects.data?.items ?? [];
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (p) =>
        p.name.toLowerCase().includes(q) || p.domain.toLowerCase().includes(q),
    );
  }, [projects.data, query]);

  const auditStats = useMemo(() => {
    const items = audits.data?.items ?? [];
    const map = new Map<string, { count: number; latest: string | null }>();
    for (const a of items) {
      const cur = map.get(a.project_id) ?? {
        count: 0,
        latest: null as string | null,
      };
      cur.count++;
      if (!cur.latest || a.created_at > cur.latest) cur.latest = a.created_at;
      map.set(a.project_id, cur);
    }
    return map;
  }, [audits.data]);

  const isEmpty = (projects.data?.items.length ?? 0) === 0;
  const noMatches = !isEmpty && filtered.length === 0;

  return (
    <Page>
      <PageHeader
        title="Projects"
        description="Each project is a site you're optimising. Pick one to dive in."
        actions={<ProjectCreateButton />}
      />

      <PageToolbar>
        <div className="relative flex-1 min-w-[200px]">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter by name or domain…"
            className="h-9 pl-8"
          />
        </div>
        <span className="text-xs text-muted-foreground">
          {filtered.length} of {projects.data?.items.length ?? 0}
        </span>
      </PageToolbar>

      {projects.error ? <ErrorBanner error={projects.error} /> : null}

      {projects.isLoading ? (
        <div className="flex justify-center p-10">
          <Spinner />
        </div>
      ) : isEmpty ? (
        <EmptyState
          icon={FolderKanban}
          title="No projects yet"
          description="Create your first project to run audits, generate briefs, and start keyword research."
          action={<ProjectCreateButton />}
        />
      ) : noMatches ? (
        <EmptyState
          icon={Search}
          title="No matches"
          description={`Nothing matches "${query}". Try a different filter.`}
        />
      ) : (
        <motion.div
          initial="hidden"
          animate="show"
          variants={stagger}
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
        >
          {filtered.map((p) => {
            const stats = auditStats.get(p.id);
            return (
              <motion.div key={p.id} variants={fadeUp}>
                <ProjectCard
                  project={p}
                  auditCount={stats?.count ?? 0}
                  lastAuditAt={stats?.latest ?? null}
                />
              </motion.div>
            );
          })}
        </motion.div>
      )}
    </Page>
  );
}

function ProjectCard({
  project,
  auditCount,
  lastAuditAt,
}: {
  project: Project;
  auditCount: number;
  lastAuditAt: string | null;
}): JSX.Element {
  const host = hostname(project.domain);
  const favicon = `https://www.google.com/s2/favicons?domain=${host}&sz=64`;

  return (
    <Link
      to="/projects/$projectId"
      params={{ projectId: project.id }}
      className="block group h-full"
    >
      <Surface
        interactive
        className="flex h-full flex-col gap-4 p-5 transition-transform group-hover:-translate-y-0.5"
      >
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 flex-none items-center justify-center overflow-hidden rounded-md border border-border/70 bg-muted/50">
            <img
              src={favicon}
              alt=""
              width={24}
              height={24}
              loading="lazy"
              referrerPolicy="no-referrer"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = 'none';
              }}
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="truncate text-sm font-semibold leading-tight">
              {project.name}
            </div>
            <div className="truncate text-xs text-muted-foreground">{host}</div>
          </div>
          <ArrowRight className="h-4 w-4 flex-none text-muted-foreground transition-transform group-hover:translate-x-0.5" />
        </div>
        <div className="mt-auto flex items-center justify-between border-t border-border/70 pt-3 text-xs">
          <Badge variant="secondary" className="font-mono">
            {auditCount} audit{auditCount === 1 ? '' : 's'}
          </Badge>
          <span className="text-muted-foreground">
            Last audit · {relTime(lastAuditAt)}
          </span>
        </div>
      </Surface>
    </Link>
  );
}
